import Box from '@mui/material/Box';
import { useCallback, useEffect, useState } from 'react';
import { FetchDeviceListRequest } from '../../../helper/cloudcore/request/models/events';
import { CloudCoreFactory } from '../../../helper/cloudcore/factory';
import { useAppDispatch, useAppSelector } from '../../../common/state/hooks';
import { RootState } from '../../../common/state';
import { ConnectedDeviceCard } from './ConnectedDeviceCard';
import { BriefGadgetInfo } from './BriefGadgetInfo';
import { FlattenedGadget } from '../../../models/GadgetModel';
import styles from "../css/ConnectedDeviceList.module.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate } from '@fortawesome/free-solid-svg-icons';
import { Tooltip, Typography } from '@mui/material';
import { notify } from '../../../common/state/Slices/NotificationSlice';
import { KeycloakClientFactory } from '../../../helper/keycloak/factory';
import { isGadgetSupportedByDevice } from '../../../helper/utils';

interface ConnectedDeviceListProps {
    displayGadget: FlattenedGadget
}

export const ConnectedDeviceList = ({displayGadget}: ConnectedDeviceListProps) => {
    const keycloak = KeycloakClientFactory.getInstance();
    const userData = keycloak.getIdToken();
    const userId = userData?.sub || "";
    const cloudCoreClient = CloudCoreFactory.getInstance();
    const dispatch = useAppDispatch();
    const connectedDeviceList = useAppSelector((state: RootState) => state.connectedDeviceSlice.deviceList);
    const supportedDevices = connectedDeviceList.filter((it) => isGadgetSupportedByDevice(displayGadget.platform, it.platform));
    const onlineDevices = supportedDevices.filter((it) => it.online);
    const [isFetching, setIsFetching] = useState<boolean>(false);

    const fetchDevices = useCallback(() => {
        if(userId === "") {
            const errorMessage = "Cannot fetch device list as userId is empty";
            dispatch(
                notify({
                    message: errorMessage,
                    severity: "error",
                    autoHideDuration: 4000
                })
            );
            console.error(errorMessage);
        } else {
            setIsFetching(true);
            cloudCoreClient.sendEvent(new FetchDeviceListRequest(userId));
        }
    }, [cloudCoreClient, userId, dispatch]);

    useEffect(() => {
        fetchDevices();
    }, [fetchDevices]);

    useEffect(() => {
        setIsFetching(false);
    }, [connectedDeviceList]);

    return (
        <Box>
            <BriefGadgetInfo displayGadget={displayGadget} />
            <Box className={styles.deviceAvailableContainer}>
                <Typography variant="h2"className={styles.deviceAvailableText}>{`${onlineDevices.length} online device(s) available`}</Typography>
                <Tooltip title="Refetch Devices" placement="left">
                    <FontAwesomeIcon icon={faRotate} spin={isFetching} size="2x" onClick={fetchDevices} className={styles.refetchIcon}/>
                </Tooltip>
            </Box>
            <Box className={styles.connectedDeviceListContainer}>
                {
                    supportedDevices.map((item, index) =>
                        <ConnectedDeviceCard 
                            device={item} 
                            dataTestId={"connected-device-card"}
                            key={`connected-device-card-${index}`}
                            displayGadget={displayGadget}
                            isOnline={item.online}
                        />
                    )
                }
            </Box>
        </Box>
    )
}